/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Filters from '../../../../client/js/components/Filters'
import ListCard from '../../../../client/js/components/ListCard'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import PageTitle from '../../../../client/js/components/PageTitle'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import useSelection from '../../../../client/js/components/selection/useSelection'
import useQueryParams, { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import RequirePerm from '../../../auth/components/RequirePerm'
import { deleteContact, findManyContacts } from '../../lib/rest/contactAPIClient'
import WithEmailFilter from '../filters/WithEmailFilter'
import WithMobileFilter from '../filters/WithMobileFilter'
import ContactList from './ContactList'
import WithPhoneFilter from '../filters/WithPhoneFilter'
import EnabledFilter from '../filters/EnabledFilter'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { getContactCreateLink } from '../../lib/links/contactLinks'
import { Button, Container } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'
import Page from '../../../../client/js/components/Page'

export type ContactListQuery = {
  limit?: string,
  page?: string,
  orderBy?: string,
  search?: string,
  enabled?: string,
  withMobile?: string,
  withPhone?: string,
  withEmail?: string,
}

const initialQuery: ContactListQuery = {
  limit: '10',
  page: '1',
  orderBy: 'name'
}

function ContactListPage () {
  const intl = useIntl()

  const title = intl.formatMessage({
    id: 'contacts',
    defaultMessage: 'Contacts'
  })

  const [searchParams, setSearchParams] = useQueryParams()
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...searchParams,
      page,
      limit
    }
  }, [limit, page, searchParams])

  // Items loading.
  const listQuery = useQuery({
    enabled: true,
    queryKey: ['contacts', queryParams],
    queryFn: () => findManyContacts(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })

  const items = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.contacts : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value?: string) => {
    setSearchParams((s: QueryParams) => ({ ...s, page: '1', [name]: value }))
  }, [setSearchParams])

  const setSearch = useCallback((search: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteContact(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <ListCard
          filters={<Filters in={isFiltersVisible}>
            <EnabledFilter
              onChange={setFilter}
              value={queryParams.enabled}
            />
            <WithMobileFilter
              onChange={setFilter}
              value={queryParams.withMobile}
            />
            <WithPhoneFilter
              onChange={setFilter}
              value={queryParams.withPhone}
            />
            <WithEmailFilter
              onChange={setFilter}
              value={queryParams.withEmail}
            />
          </Filters>}
          pagination={pagination}
          selection={selection}
          selectionActions={<SelectionAlert>
            <RequirePerm
              permissions={['write:contact']}
              render={() => (
                <Button onClick={toggleDeleteModal}>
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faTrash}
                  />
                </Button>
              )}
            />
          </SelectionAlert>}
          toolbar={<Toolbar>
            <RequirePerm
              permissions={['write:contact']}
              render={() => (
                <Button
                  variant="filled"
                  component={AnchorLink}
                  to={getContactCreateLink()}
                >
                  <FormattedMessage
                    id="create_contact"
                    defaultMessage="Créer un contact"
                  />
                </Button>
              )}
            />
            <SearchForm
              initialValue={queryParams.search}
              onSubmit={setSearch}
              submitting={listQuery.isFetching}
            />
            <Button
              loading={listQuery.isFetching}
              onClick={() => {
                listQuery.refetch()
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faSyncAlt}
              />
            </Button>
            <Button
              // todo active={isFiltersVisible}
              onClick={toggleFilters}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faFilter}
              />
            </Button>
          </Toolbar>}
        >
          <LoadingHelper
            query={listQuery}
            render={() => (
              <ContactList items={items} />
            )}
          />
        </ListCard>
      </Container>

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          onClose={toggleDeleteModal}
        />
        : null}
    </Page>
  )
}

export default ContactListPage
